import {
  isAfter,
  isBefore,
  isDate,
  subMinutes,
  subHours,
  addHours
} from 'date-fns';

export const SessionStatus = Object.freeze({
  BEFORE: 'BEFORE',
  MORE_THAN_ONE_HOUR_BEFORE: 'MORE_THAN_ONE_HOUR_BEFORE',
  MORE_THAN_FIVE_MIN_BEFORE: 'MORE_THAN_FIVE_MIN_BEFORE',
  MORE_THAN_TEN_MIN_BEFORE: 'MORE_THAN_TEN_MIN_BEFORE',
  LESS_THAN_TEN_MIN_BEFORE: 'LESS_THAN_TEN_MIN_BEFORE',
  LESS_THAN_FIVE_MIN_BEFORE: 'LESS_THAN_FIVE_MIN_BEFORE',
  DURING: 'DURING',
  LESS_THAN_FIVE_MIN_BEFORE_END: 'LESS_THAN_FIVE_MIN_BEFORE_END',
  LESS_THAN_ONE_MIN_BEFORE_END: 'LESS_THAN_ONE_MIN_BEFORE_END',
  LESS_THAN_ONE_HOUR_AFTER: 'LESS_THAN_ONE_HOUR_AFTER',
  MORE_THAN_ONE_HOUR_AFTER: 'MORE_THAN_ONE_HOUR_AFTER',
  AFTER: 'AFTER' // Deprecated
});

/**
 * Hook to determine the session status based on start/end time
 *
 * @param {Date | string} now - Current user's local time
 * @param {Date | string} startTime - Session Start Time
 * @param {Date | string} endTime - Session End Time
 * @param {Object} [options = {}] - Additional flags to return particular session statuses
 * @param {boolean} [options.useDetailedBeforeStatus = false] - Flag to return the detailed 'before' session statuses if set to true, i.e. MORE_THAN_TEN_MIN_BEFORE and LESS_THAN_TEN_MIN_BEFORE. Defaults to false.
 * @param {boolean} [options.useDetailedDuringStatus = false] - Flag to return the detailed 'during' session statuses if set to true, i.e. LESS_THAN_FIVE_MIN_BEFORE_END and LESS_THAN_ONE_MIN_BEFORE_END. Defaults to false.
 * @param {boolean} [options.useDetailedAfterStatus = false] - Flag to return the detailed 'after' session statuses if set to true, i.e. LESS_THAN_ONE_HOUR_AFTER and MORE_THAN_ONE_HOUR_AFTER. Defaults to false.
 * @return {string} One of the session status strings from SessionStatus
 */
const getSessionStatus = (now, startTime, endTime, options = {}) => {
  const parsedNowTime = isDate(now) ? now : Date.parse(now);
  const parsedStartTime = isDate(startTime) ? startTime : Date.parse(startTime);
  const parsedEndTime = isDate(endTime) ? endTime : Date.parse(endTime);

  // Flags to determine whether to use the more detailed sessions statuses or not. Default is to use regular statuses.
  const {
    useDetailedBeforeStatus = false,
    useDetailedDuringStatus = false,
    useDetailedAfterStatus = false
  } = options;

  // Before Session Start
  if (isBefore(parsedNowTime, subHours(parsedStartTime, 1))) {
    // if current time is before (session start - 1 hour)
    return SessionStatus.MORE_THAN_ONE_HOUR_BEFORE;
  }

  if (useDetailedBeforeStatus) {
    if (isBefore(parsedNowTime, subMinutes(parsedStartTime, 10))) {
      // if current time is more than 10 minutes before session start
      return SessionStatus.MORE_THAN_TEN_MIN_BEFORE;
    }
    if (isBefore(parsedNowTime, subMinutes(parsedStartTime, 5))) {
      // if current time is less than 10 minutes and more than 5 minutes before session start time
      return SessionStatus.LESS_THAN_TEN_MIN_BEFORE;
    }
    if (isBefore(parsedNowTime, parsedStartTime)) {
      // if current time is less than 5 minutes and before the session start
      return SessionStatus.LESS_THAN_FIVE_MIN_BEFORE;
    }
  } else {
    if (isBefore(parsedNowTime, subMinutes(parsedStartTime, 5))) {
      // if current time is more than 5 minutes before session start
      return SessionStatus.MORE_THAN_FIVE_MIN_BEFORE;
    }
    if (isBefore(parsedNowTime, parsedStartTime)) {
      // if current time is less than 5 minutes and before the session start
      return SessionStatus.LESS_THAN_FIVE_MIN_BEFORE;
    }
  }

  // After Session Start and Before Session End
  if (useDetailedDuringStatus) {
    if (isBefore(parsedNowTime, subMinutes(parsedEndTime, 5))) {
      // if current time is more than 5 minutes remaining
      return SessionStatus.DURING;
    }
    if (isBefore(parsedNowTime, subMinutes(parsedEndTime, 1))) {
      // if current time is less than 5 minutes and more than 1 minute remaining
      return SessionStatus.LESS_THAN_FIVE_MIN_BEFORE_END;
    }
    if (isBefore(parsedNowTime, parsedEndTime)) {
      // if current time is less than 1 minute remaining and before session end
      return SessionStatus.LESS_THAN_ONE_MIN_BEFORE_END;
    }
  } else if (isBefore(parsedNowTime, parsedEndTime)) {
    // if current time is before session end
    return SessionStatus.DURING;
  }

  // After Session End
  if (useDetailedAfterStatus) {
    if (isBefore(parsedNowTime, addHours(parsedEndTime, 1))) {
      // if current time is before (session end + 1 hour)
      return SessionStatus.LESS_THAN_ONE_HOUR_AFTER;
    }
    // else current time is after session end + 1 hour
    return SessionStatus.MORE_THAN_ONE_HOUR_AFTER;
  }

  // else current time is after session end, return AFTER
  return SessionStatus.AFTER;
};

/**
 * Utility function to determine if the session is ended with optional buffer.
 */
export const isSessionEnded = (now, endTime, buffer = 0) => {
  const parsedEndTime = isDate(endTime) ? endTime : Date.parse(endTime);
  return isAfter(now, subMinutes(parsedEndTime, buffer));
};

/**
 * Utility function to determine if session is in after state
 */
export const isAfterSessionStatus = sessionStatus => {
  return [
    SessionStatus.AFTER,
    SessionStatus.LESS_THAN_ONE_HOUR_AFTER,
    SessionStatus.MORE_THAN_ONE_HOUR_AFTER
  ].includes(sessionStatus);
};
/**
 * Utility function to return the shorthand sessionStatus i.e. BEFORE, DURING, or AFTER
 */
export const getSessionShortStatus = sessionStatus => {
  switch (sessionStatus) {
    case SessionStatus.AFTER:
    case SessionStatus.LESS_THAN_ONE_HOUR_AFTER:
    case SessionStatus.MORE_THAN_ONE_HOUR_AFTER:
      return SessionStatus.AFTER;
    case SessionStatus.MORE_THAN_ONE_HOUR_BEFORE:
    case SessionStatus.MORE_THAN_FIVE_MIN_BEFORE:
    case SessionStatus.LESS_THAN_FIVE_MIN_BEFORE:
      return SessionStatus.BEFORE;
    default:
      return SessionStatus.DURING;
  }
};
export default getSessionStatus;
